import { Output, Injectable, EventEmitter } from '@angular/core';
import { BreakpointObserver } from '@angular/cdk/layout';
import { BehaviorSubject, fromEvent } from 'rxjs';
import { debounceTime, map, startWith } from 'rxjs/operators';

@Injectable()
export class ScreenService {
  @Output() changed = new EventEmitter();

  private resolutionSource = new BehaviorSubject<{
    width: number;
    height: number;
  }>({
    width: window.innerWidth,
    height: window.innerHeight,
  });

  private screenModeSource = new BehaviorSubject<string>('');

  resolution$ = this.resolutionSource.asObservable();
  screenMode$ = this.screenModeSource.asObservable();

  XSmall = '(max-width: 599px)';
  Small = '(min-width: 600px) and (max-width: 959px)';
  Medium = '(min-width: 960px) and (max-width: 1339px)';
  Large = '(min-width: 1340px) and (max-width: 1919px)';
  XLarge = '(min-width: 1920px)';

  constructor(private breakpointObserver: BreakpointObserver) {
    this.breakpointMonitoring();
    this.screenResizeMonitoring();
  }

  private breakpointMonitoring() {
    this.breakpointObserver
      .observe([this.XSmall, this.Small, this.Medium, this.Large])
      .subscribe(() => this.changed.next());
  }

  private breakpointObserving() {
    return this.breakpointObserver.observe([
      this.XSmall,
      this.Small,
      this.Medium,
      this.Large,
    ]);
  }

  private screenResizeMonitoring() {
    fromEvent(window, 'resize')
      .pipe(
        debounceTime(200),
        map(() => ({ width: window.innerWidth, height: window.innerHeight })),
        startWith({ width: window.innerWidth, height: window.innerHeight }),
      )
      .subscribe(this.resolutionSource);
  }

  private isLargeScreen() {
    const isLarge = this.breakpointObserver.isMatched(this.Large);
    const isXLarge = this.breakpointObserver.isMatched(this.XLarge);
    return isLarge || isXLarge;
  }

  public get sizes() {
    return {
      'screen-x-small': this.breakpointObserver.isMatched(this.XSmall),
      'screen-small': this.breakpointObserver.isMatched(this.Small),
      'screen-medium': this.breakpointObserver.isMatched(this.Medium),
      'screen-large': this.isLargeScreen(),
    };
  }
}
